'use client';

import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@pickleballinc/react-ui';
import { useRouter } from 'next/navigation';
import { ChangeEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';

const venuesList = ['All', 'Barnes Tennis Center', 'Pickleball Club of Carlsbad', 'Bobby Riggs Tennis Club'];

const mensDoublesTeams = ['Pickleball Pros1', 'PPA Pickleballer2', 'Denver Pickleball Team3'];
const womensDoublesTeams = ['Pickleball Pros1', 'PPA Pickleballer2', 'Denver Pickleball Team3'];

interface ScheduleFiltersData {
	venues: string[];
	mensDoubles: string[];
	womensDoubles: string[];
}

export const ScheduleFilters = () => {
	const router = useRouter();

	const { watch, setValue } = useForm<ScheduleFiltersData>({
		defaultValues: {
			venues: [],
			mensDoubles: [],
			womensDoubles: []
		}
	});

	const selectedVenues = watch('venues');
	const selectedMens = watch('mensDoubles');
	const selectedWomens = watch('womensDoubles');

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);

		const venuesParam = params.get('venues');
		const mensParam = params.get('mensDoubles');
		const womensParam = params.get('womensDoubles');

		if (venuesParam) setValue('venues', venuesParam.split(','));
		if (mensParam) setValue('mensDoubles', mensParam.split(','));
		if (womensParam) setValue('womensDoubles', womensParam.split(','));
	}, [setValue]);

	const allSelectedVenues = selectedVenues.length === venuesList.length;
	const allMensSelected = selectedMens.length === mensDoublesTeams.length;
	const allWomensSelected = selectedWomens.length === womensDoublesTeams.length;

	const updateURL = () => {
		const params = new URLSearchParams(window.location.search);

		if (selectedVenues.length > 0) {
			params.set('venues', selectedVenues.join(','));
		} else {
			params.delete('venues');
		}

		if (selectedMens.length > 0) {
			params.set('mensDoubles', selectedMens.join(','));
		} else {
			params.delete('mensDoubles');
		}

		if (selectedWomens.length > 0) {
			params.set('womensDoubles', selectedWomens.join(','));
		} else {
			params.delete('womensDoubles');
		}

		const queryString = params.toString();

		router.replace(`?${queryString}`);
	};

	useEffect(() => {
		updateURL();
	}, [selectedVenues, selectedMens, selectedWomens]);

	const handleAllVenuesChange = (checked: boolean) => setValue('venues', checked ? venuesList : []);
	const handleAllMensChange = (checked: boolean) => setValue('mensDoubles', checked ? mensDoublesTeams : []);
	const handleAllWomensChange = (checked: boolean) => setValue('womensDoubles', checked ? womensDoublesTeams : []);

	return (
		<div className="flex flex-col gap-6 p-2 ">
			<div>
				<p className=" text-sm font-semibold">Venues</p>
				<div className="flex flex-col gap-2 pt-2">
					<div className="flex items-center">
						<Checkbox
							theme="orange"
							className="size-5"
							checked={allSelectedVenues}
							onChange={(e: ChangeEvent<HTMLInputElement>) => handleAllVenuesChange(e.target.checked)}
						/>
						<p className="pl-2 text-sm">All</p>
					</div>
					{venuesList.slice(1).map((venue) => (
						<div key={venue} className="flex items-center">
							<Checkbox
								theme="orange"
								className="size-5 cursor-pointer"
								checked={selectedVenues.includes(venue)}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									const checked = e.target.checked;
									const currentVenues = selectedVenues;
									setValue('venues', checked ? [...currentVenues, venue] : currentVenues.filter((v) => v !== venue));
								}}
							/>
							<p className="pl-2 text-xs">{venue}</p>
						</div>
					))}
				</div>
			</div>

			<div>
				<p className="flex items-center justify-between gap-2 text-sm font-semibold">
					Divisions
					<FontAwesomeIcon icon={faGear} className="size-6 text-gray-600" />
				</p>

				<div className="pt-2">
					<p className="text-sm font-semibold text-purple-700">Mens doubles 3.5|50+</p>
					<div className="flex items-center pl-4 pt-2">
						<Checkbox
							theme="orange"
							className="size-5"
							checked={allMensSelected}
							onChange={(e: ChangeEvent<HTMLInputElement>) => handleAllMensChange(e.target.checked)}
						/>
						<p className="pl-2">Mens doubles 3.5|50+</p>
					</div>
					<div className="flex flex-col gap-2 pl-8 pt-2">
						{mensDoublesTeams.map((team) => (
							<div key={team} className="flex items-center">
								<Checkbox
									theme="orange"
									className="size-5 cursor-pointer"
									checked={selectedMens.includes(team)}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										const checked = e.target.checked;
										const currentMens = selectedMens;
										setValue('mensDoubles', checked ? [...currentMens, team] : currentMens.filter((t) => t !== team));
									}}
								/>
								<p className="pl-2 text-xs">{team}</p>
							</div>
						))}
					</div>
				</div>

				<div className="pt-6">
					<p className="text-sm font-semibold text-purple-700">Womens doubles 4.0|25+</p>
					<div className="flex items-center pl-4 pt-2">
						<Checkbox
							theme="orange"
							className="size-5"
							checked={allWomensSelected}
							onChange={(e: ChangeEvent<HTMLInputElement>) => handleAllWomensChange(e.target.checked)}
						/>
						<p className="pl-2">All</p>
					</div>
					<div className="flex flex-col gap-2 pl-8 pt-2">
						{womensDoublesTeams.map((team) => (
							<div key={team} className="flex items-center">
								<Checkbox
									theme="orange"
									className="size-5 cursor-pointer"
									checked={selectedWomens.includes(team)}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										const checked = e.target.checked;
										const currentWomens = selectedWomens;
										setValue('womensDoubles', checked ? [...currentWomens, team] : currentWomens.filter((t) => t !== team));
									}}
								/>
								<p className="pl-2 text-xs">{team}</p>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
